import React, { useState } from "react";
import { Icons } from "../../constants/icon-library";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  createSubscriptionSession,
  cancelStripeSubscription,
} from "../../features/transaction/transactionAPI";
import { useSelector } from "react-redux";
import { confirmationModal } from "../../constants/confirmation.constants";
import { toast } from "react-toastify";

const override = {
  display: "",
  margin: "0 auto",
  textAlign: "center",
  padding: "20px",
};

function Pricing() {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { organization } = useSelector((state) => state.organization);

  const currentPlan = organization?.subscriptionPlan;
  const navigate = useNavigate();

  const cancelSubscription = async () => {
    const result = await confirmationModal({
      title: "Are you sure?",
      text: "Your subscription will be canceled and future renewals will stop.",
      confirmText: "Yes, Cancel",
      cancelText: "Keep Subscription",
    });

    if (result.isConfirmed) {
      await cancelStripeSubscription();
      navigate("/billing");
      window.location.reload();
      toast.success("Subscription cancelled successfully.");
    }
  };

  const onButtonClick = async (plan, priceId) => {
    setLoadingPlan(plan);
    const subscriptionSession = await createSubscriptionSession({
      subscriptionPlan: plan,
      priceId,
    });
    navigate(subscriptionSession.url);
    setLoadingPlan(null);
  };

  const checkTransactions = () => {
    navigate("/billing");
  };

  const planDetails = () => {
    navigate("/plan-details");
  };

  const addCredits = () => {
    navigate("/add-credits");
  };

  return (
    <div className="container py-12">
      <div className="card border-0 shadow-lg text-white">
        <div className="card-body p-4 p-md-5">
          <div className="row">
            <div className="d-flex justify-content-end gap-2 mb-5">
              <button
                onClick={() => checkTransactions()}
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <i className="wb webconnect-receipt"></i>
                Transactions
              </button>

              <button
                onClick={() => planDetails()}
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <i className="wb webconnect-credit-card"></i>
                Plan Details
              </button>

              <button
                onClick={() => addCredits()}
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <i className="wb webconnect-plus-circle"></i>
                Buy More Credits
              </button>
            </div>
            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-0 shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2">Personal Plan</h3>
                    <p className="text-muted">
                      Perfect for individuals & small teams getting started with
                      meeting-to-task automation.
                    </p>
                  </div>

                  <div className="my-3">
                    <span
                      className="text-muted me-1"
                      style={{ fontSize: "1.5rem" }}
                    >
                      $
                    </span>
                    <span className="fw-bold" style={{ fontSize: "3rem" }}>
                      19
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 rounded-pill">✔ 10,000 Tokens</li>
                    <li className="mb-2 text-muted">
                      ✔ Up to 200 hours of conversation processing
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Up to 2,000 task identifications & creations
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Integration with all tools (Asana, Jira, Trello, Slack,
                      and more)
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      onClick={() => {
                        if (currentPlan === "SUBSCRIPTION_1") return;
                        onButtonClick(
                          "SUBSCRIPTION_1",
                          process.env.REACT_APP_STRIPE_PERSONAL_PLAN,
                        );
                      }}
                      disabled={
                        loadingPlan === "SUBSCRIPTION_1" ||
                        currentPlan === "SUBSCRIPTION_1"
                      }
                      className="btn btn-outline-primary btn-lg w-100 text-nowrap"
                    >
                      <i className={`wb ${Icons("subscribe")} me-2`}></i>
                      {loadingPlan === "SUBSCRIPTION_1"
                        ? "Processing"
                        : currentPlan === "SUBSCRIPTION_1"
                          ? "Subscribed"
                          : "Subscribe"}
                      <BeatLoader
                        loading={loadingPlan === "SUBSCRIPTION_1"}
                        cssOverride={override}
                        color="var(--phoenix-emphasis-color)"
                        size="8px"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-primary shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2 d-flex align-items-center gap-2">
                      Business Plan
                      <span
                        className="badge rounded-pill"
                        style={{ backgroundColor: "#162f4b" }}
                      >
                        #Popular
                      </span>
                    </h3>
                    <p className="text-muted">
                      Designed for growing teams with higher meeting volumes.
                    </p>
                  </div>

                  <div className="my-3">
                    <span
                      className="text-muted me-1"
                      style={{ fontSize: "1.5rem" }}
                    >
                      $
                    </span>
                    <span className="fw-bold" style={{ fontSize: "3rem" }}>
                      49
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 rounded-pill">✔ 30,000 Tokens</li>
                    <li className="mb-2">
                      ✔ Up to 500 hours of conversation processing
                    </li>
                    <li className="mb-2">
                      ✔ Up to 6,000 task identifications & creations
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Integration with all tools (Asana, Jira, Trello, Slack,
                      and more)
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      onClick={() => {
                        if (currentPlan === "SUBSCRIPTION_2") return;
                        onButtonClick(
                          "SUBSCRIPTION_2",
                          process.env.REACT_APP_STRIPE_BUSINESS_PLAN,
                        );
                      }}
                      disabled={
                        loadingPlan === "SUBSCRIPTION_2" ||
                        currentPlan === "SUBSCRIPTION_2"
                      }
                      className="btn btn-primary btn-lg w-100 text-nowrap"
                    >
                      <i className={`wb ${Icons("subscribe")} me-2`}></i>
                      {loadingPlan === "SUBSCRIPTION_2"
                        ? "Processing"
                        : currentPlan === "SUBSCRIPTION_2"
                          ? "Subscribed"
                          : "Subscribe"}
                      <BeatLoader
                        loading={loadingPlan === "SUBSCRIPTION_2"}
                        cssOverride={override}
                        color="var(--phoenix-emphasis-color)"
                        size="8px"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-0 shadow pricing-card">
                <div className="card-body p-4 d-flex flex-column">
                  <div>
                    <h3 className="mb-2">Enterprise Plan</h3>
                    <p className="text-muted">
                      Built for organizations needing scalable automation.
                    </p>
                  </div>

                  <div className="" style={{ marginTop: "3rem" }}>
                    <span
                      className="fw-bold"
                      style={{
                        fontSize: "2.5rem",
                      }}
                    >
                      Custom
                    </span>
                  </div>

                  <ul className="list-unstyled mb-4 mt-4">
                    <li className="mb-2 rounded-pill">
                      ✔ Custom token limits on usage
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Unlimited or high-volume conversation processing
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ Advanced task extraction & bulk assignment capabilities
                    </li>
                    <li className="mb-2 text-muted">
                      ✔ All integrations + priority support + custom workflows
                    </li>
                  </ul>

                  <div className="mt-auto">
                    <button
                      className="btn btn-outline-primary btn-lg w-100 text-nowrap"
                      onClick={() =>
                        window.open("https://steertask.com/contact", "_blank")
                      }
                    >
                      <i className="wb webconnect-envelope me-2"></i>
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {(currentPlan === "SUBSCRIPTION_1" ||
            currentPlan === "SUBSCRIPTION_2") && (
            <button
              onClick={() => cancelSubscription()}
              type="button"
              className="btn btn-danger d-flex align-items-center gap-1 mt-4"
              style={{ float: "inline-end" }}
            >
              <i className="wb webconnect-x-circle-fill mt-1"></i>
              Cancel Subscription
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Pricing;
